import api from './api.service';
import { TenantDetail, TenantCheckInRequest, TenantGiveNoticeRequest, TenantCheckOutRequest, RoomSummary, AgencyRoom, AgencyOwnDocument } from '../types/tenant';
import { UserResponse } from '../types/user';

class TenantsService {
  async listForColoc(colocId: string): Promise<TenantDetail[]> {
    const response = await api.get(`/tenants/coloc/${colocId}`);
    return response.data;
  }

  /** Dossier du locataire connecté (pas besoin d'identifiant technique). */
  async getMyDossier(): Promise<TenantDetail> {
    const response = await api.get('/tenants/me');
    return response.data;
  }

  /** Active / coupe l'accès d'un locataire à son espace (propriétaire uniquement). */
  async setAccess(tenantInfoId: string, enabled: boolean): Promise<any> {
    const response = await api.put(`/tenants/${tenantInfoId}/access`, { enabled });
    return response.data;
  }

  async getDetail(tenantInfoId: string): Promise<TenantDetail> {
    const response = await api.get(`/tenants/${tenantInfoId}`);
    return response.data;
  }

  /** Le dossier tel que le locataire le reçoit, pour l'aperçu « Voir son espace ». */
  async getVueLocataire(tenantInfoId: string): Promise<TenantDetail> {
    const response = await api.get(`/tenants/${tenantInfoId}`, { params: { vue_locataire: true } });
    return response.data;
  }

  /** Crée un compte locataire. Aucun secret transmis : l'API envoie un lien de
   *  création de mot de passe, que le bailleur ne connaîtra jamais. */
  /** Crée un compte locataire. `send_welcome` commande l'envoi du lien de
   *  connexion — faux par défaut côté API : créer n'écrit à personne. */
  async createTenant(data: {
    first_name: string;
    last_name: string;
    email: string;
    coloc_ids: string[];
    send_welcome: boolean;
  }): Promise<UserResponse & { invitationSent: boolean }> {
    const response = await api.post('/tenants/', data);
    return response.data;
  }

  async checkIn(userId: string, data: TenantCheckInRequest): Promise<any> {
    const response = await api.post(`/tenants/${userId}/check-in`, data);
    return response.data;
  }

  async giveNotice(tenantInfoId: string, data: TenantGiveNoticeRequest): Promise<any> {
    const response = await api.post(`/tenants/${tenantInfoId}/give-notice`, data);
    return response.data;
  }

  /** Réservation faite à la main, bail pas encore commencé (#17). */
  async cancelReservation(tenantInfoId: string): Promise<void> {
    await api.post(`/tenants/${tenantInfoId}/cancel-reservation`);
  }

  async cancelNotice(tenantInfoId: string): Promise<any> {
    const response = await api.post(`/tenants/${tenantInfoId}/cancel-notice`);
    return response.data;
  }

  /**
   * Date d'état des lieux d'ENTRÉE. Séparée du téléversement du bail parce que
   * les deux n'arrivent presque jamais ensemble : l'état des lieux se fait à la
   * remise des clés, le bail signé revient de l'agence quand il revient. Les
   * deux réunis ouvrent l'accès du locataire à son espace.
   */
  async setEntryInventory(tenantInfoId: string, date: string): Promise<any> {
    const response = await api.post(`/tenants/${tenantInfoId}/entry-inventory`, {
      entry_inventory_date: date,
    });
    return response.data;
  }

  async checkOut(tenantInfoId: string, data: TenantCheckOutRequest): Promise<any> {
    const response = await api.post(`/tenants/${tenantInfoId}/check-out`, data);
    return response.data;
  }

  async uploadDocument(
    tenantInfoId: string,
    file: File,
    documentType: string,
    notes?: string,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    if (notes) formData.append('notes', notes);

    const response = await api.post(`/tenants/${tenantInfoId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /** Les pièces que CETTE agence a elle-même déposées dans ce dossier.
   *  Elle ne voit rien d'autre du dossier : ni le bail, ni les pièces de
   *  candidature, ni ce que le propriétaire y a mis. */
  async listOwnAgencyDocuments(tenantInfoId: string): Promise<AgencyOwnDocument[]> {
    const response = await api.get(`/tenants/${tenantInfoId}/documents/mine`);
    return response.data;
  }

  /** Retire une pièce du dossier d'un locataire.
   *
   *  Le backend décide qui peut quoi : le propriétaire retire n'importe quelle
   *  pièce (sauf le PDF d'un bail SIGNÉ, qui répond 409), une agence retire
   *  uniquement ce qu'elle a elle-même déposé. Une seule route, deux droits. */
  async deleteDocument(tenantInfoId: string, docId: string): Promise<void> {
    await api.delete(`/tenants/${tenantInfoId}/documents/${docId}`);
  }

  async downloadDocument(tenantInfoId: string, docId: string): Promise<Blob> {
    const response = await api.get(`/tenants/${tenantInfoId}/documents/${docId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /** Lots des colocations partagées avec l'agence connectée (dépôt de pièces). */
  /** (Re)envoie au locataire son lien de création de mot de passe.
   *
   *  Seule voie de rattrapage depuis que les formulaires de création ne
   *  prennent plus de mot de passe : un lien expiré ou un email perdu laisse
   *  sinon le locataire dehors. Chaque envoi émet un jeton neuf SANS invalider
   *  les précédents, pour ne pas casser un lien en cours d'utilisation. */
  async resendWelcomeEmail(tenantInfoId: string): Promise<{ sent: boolean }> {
    const response = await api.post(`/tenants/${tenantInfoId}/welcome-email`, {});
    return response.data;
  }

  /** Corrige les conditions d'un bail : montants et date de début.
   *
   *  Correction de saisie, rien de plus — ces valeurs n'étaient écrites qu'à la
   *  création du dossier et jamais réaffectées, donc une faute de frappe était
   *  définitive. Seuls les champs envoyés sont modifiés. */
  async updateLeaseTerms(
    tenantInfoId: string,
    data: {
      lease_start_date?: string;
      monthly_rent?: number;
      charges_provision?: number;
      deposit_amount?: number;
      agency_expenses?: number;
      /** Toujours transmis, `null` compris : « non renseigné » est une valeur
       *  qu'on doit pouvoir remettre, pas seulement quitter. */
      guarantor_type?: string | null;
      /** Le nom du compte du locataire ; vide = « ne touche pas ». */
      first_name?: string;
      last_name?: string;
    },
  ): Promise<TenantDetail> {
    const response = await api.put(`/tenants/${tenantInfoId}/lease-terms`, data);
    return response.data;
  }

  async listAgencyRooms(): Promise<AgencyRoom[]> {
    const response = await api.get('/tenants/agency/rooms');
    return response.data;
  }

  async listRooms(colocId: string): Promise<RoomSummary[]> {
    const response = await api.get(`/tenants/coloc/${colocId}/rooms`);
    return response.data;
  }
}

export const tenantsService = new TenantsService();
